/**
 * Centralized platform detection utility.
 * Detects iOS, Android, or Desktop and provides platform-specific helpers.
 * Used across HRH, Device Integration, and other platform-aware components.
 */

const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';

export const isIOS = /iPhone|iPad|iPod/i.test(ua);
export const isAndroid = /Android/i.test(ua);
export const isMobile = isIOS || isAndroid;
export const isDesktop = !isMobile;

/**
 * Returns the current platform as a string: 'ios', 'android', or 'desktop'
 */
export const getPlatform = () => {
  if (isIOS) return 'ios';
  if (isAndroid) return 'android';
  return 'desktop';
};

/**
 * Returns the mobile health platform name for the current device.
 * iOS → "Apple Health", Android → "Google Health Connect", Desktop → null
 */
export const getHealthPlatformName = () => {
  if (isIOS) return 'Apple Health';
  if (isAndroid) return 'Google Health Connect';
  return null;
};

/**
 * Returns the HHS app store URL for the current platform.
 */
export const getHHSAppStoreUrl = () => {
  if (isIOS) return 'https://apps.apple.com/app/healist-health-sync/id6742478681';
  if (isAndroid) return null; // TODO: Add Google Play URL when Android HHS ships (Phase 8)
  return null;
};

/**
 * Returns platform-specific instructions for connecting devices.
 */
export const getDeviceConnectionInstructions = () => {
  if (isIOS) return [
    'Open your device\'s companion app (Garmin Connect, WHOOP, Oura, etc.)',
    'Go to Settings → enable "Share with Apple Health"',
    'Open HealistHealthSync — data syncs automatically every 2 hours',
    'View your data in Healist.AI from any device'
  ];
  if (isAndroid) return [
    'Open your device\'s companion app (Garmin Connect, WHOOP, Oura, etc.)',
    'Go to Settings → enable "Share with Google Health Connect"',
    'Open HealistHealthSync — data syncs automatically every 2 hours',
    'View your data in Healist.AI from any device'
  ];
  return [
    'Download HealistHealthSync on your iPhone (App Store) or Android (Google Play)',
    'Open your device\'s companion app and enable sharing with Apple Health or Google Health Connect',
    'HealistHealthSync syncs your data to Healist automatically every 2 hours',
    'View your data here in Healist.AI from any device'
  ];
};

/**
 * Returns platform-specific instructions for connecting health records.
 */
export const getRecordsConnectionInstructions = () => {
  if (isIOS) return [
    'Download HealistHealthSync from the App Store',
    'Log in with your Healist.AI credentials',
    'Tap "Connect Health Records" when prompted (or Settings → Add Health Records Provider)',
    'Add your hospitals, labs, and clinics — search by name and sign in with your patient portal credentials',
    'Records sync automatically every 2 hours, or tap Sync Now in HHS'
  ];
  if (isAndroid) return [
    'Download HealistHealthSync from Google Play',
    'Log in with your Healist.AI credentials',
    'Follow the prompts to connect your health records providers',
    'Records sync automatically every 2 hours'
  ];
  return [
    'Download HealistHealthSync on your iPhone (App Store) or Android (Google Play)',
    'Log in with your Healist.AI credentials',
    'Tap "Connect Health Records" and add your hospitals, labs, and clinics',
    'Records sync automatically every 2 hours',
    'View your records here in Healist.AI from any device'
  ];
};
